import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlarmClock, ArrowRight, ArrowUpRight, Check, ChevronRight, EyeOff, MoreHorizontal, Pencil, Plus, X } from "lucide-react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  deleteField,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type QuerySnapshot,
} from "firebase/firestore";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavbar } from "./DashboardLayout";
import { CompanyLogo } from "../components/dreamShared";
import NewRolesCard from "../components/NewRolesCard";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { MENU_ITEM } from "../components/applicationShared";
import { MELT_BUTTON, MELT_BUTTON_QUIET, MELT_DIALOG } from "../components/ui/melt";
import FeedbackDialog from "../components/FeedbackDialog";
import UndoToast, { useUndoToast } from "../components/UndoToast";
import { DatePicker } from "../components/ui/date-picker";
import { toApplications, type ApiItem } from "../utils/applicationCards";
import { useAutoSync, useSyncing } from "../utils/gmailSync";
import { beginGmailOAuth } from "../utils/gmailOAuth";
import {
  compareDreamCompanies,
  daysUntil,
  openRoles,
  plural,
  trackingState,
  unseenNewRoles,
  type DreamCompany,
  type OpenRole,
} from "../utils/dreamCompanies";
import {
  TASK_TABS,
  attentionTitle,
  applySnooze,
  autoTarget,
  autoTodos,
  dailyPrepTodo,
  compareTasks,
  deadlineDays,
  dueLabel,
  seasonSummary,
  opensToDone,
  localDay,
  setupTodos,
  tickedLast,
  todayLimit,
  withinLimit,
  greeting,
  stageDeadline,
  keepApplying,
  weeklyGoal,
  APPLY_TARGET,
  DEFAULT_WEEKLY_GOAL,
  STARTER_DREAM_COMPANIES,
  type WeekGoal,
  sinceLabel,
  sinceSummary,
  nextVisit,
  type HomeVisit,
  snoozeOptions,
  taskTab,
  limitFollowUps,
  pickBanner,
  withAutoState,
  type Banner,
  type BannerEntry,
  bannerReturnDay,
  bannerTone,
  type AutoPage,
  type AutoTodoState,
  type FunnelStep,
  type Task,
  type TaskTab,
} from "../utils/home";
import { isCodingOa } from "../utils/prepEvents";
import { usePrepPlan } from "../utils/usePrepPlan";
import { cn } from "../lib/utils";

const CARD = "rounded-2xl border border-melt-line bg-melt-paper";
/** Done to-dos shown before "See more" */
const DONE_PREVIEW = 5;

function timeAgo(ms: number, now = Date.now()) {
  const mins = Math.floor((now - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

function firstName(user: User | null) {
  return (user?.displayName || "").trim().split(/\s+/)[0] || "";
}

// ---------------------------------------------------------------- needs attention

const BANNER_TONES = {
  urgent: {
    box: "bg-[#5A3A1E] text-white",
    badge: "bg-melt-caramel text-white",
    sub: "text-white/75",
    done: "bg-white text-melt-text1",
    icon: "text-white/70 hover:bg-white/10 hover:text-white",
  },
  soon: {
    box: "bg-[#EFC48E] text-melt-text1",
    badge: "bg-melt-caramel text-white",
    sub: "text-melt-text2",
    done: "bg-[#5A3A1E] text-white",
    icon: "text-melt-text2 hover:bg-black/5 hover:text-melt-text1",
  },
  calm: {
    box: "border border-[#EBD3B0] bg-melt-cream text-melt-text1",
    badge: "bg-melt-caramel text-white",
    sub: "text-melt-text2",
    done: "bg-[#5A3A1E] text-white",
    icon: "text-melt-text2 hover:bg-black/5 hover:text-melt-text1",
  },
} as const;

/** The banner's main button for an OA: "Start prep", or "Continue prep" once Interview Prep is set up */
type BannerPrep = { label: string; go: () => void };

/**
 * One OA or interview. Its to-do moves here from the list. One button: prep
 * for an OA (Interview Prep), Done otherwise. The title opens the card; the
 * ⋯ menu has Done, Open in Applications, and Hide (until it's closer to the
 * deadline). Dark when it's 0–2 days out, lighter with more time.
 */
function AttentionRow({
  entry,
  more,
  expanded,
  onToggleMore,
  onDone,
  onOpen,
  onHide,
  onPrep,
}: {
  entry: BannerEntry;
  /** Others waiting behind this one (only on the first row) */
  more?: number;
  expanded?: boolean;
  onToggleMore?: () => void;
  onDone: () => void;
  onOpen: () => void;
  onHide: () => void;
  /** Left out when there's no prep for it (not an OA, or "I'll prep myself") */
  onPrep?: BannerPrep;
}) {
  const { item, todo } = entry;
  const days = item.deadline ? deadlineDays(item.deadline) : null;
  const tone = BANNER_TONES[bannerTone(entry)];
  const role = item.card.role?.trim();
  // The role, and the list's reason without when the email came ("OA arrived
  // Oct 3 · due date you set" → "due date you set"): the badge has the timing
  const why = todo.reason?.replace(/^OA arrived [^·]*(· )?/, "").trim();
  const sub = [role, why].filter(Boolean).join(" · ");
  const back = shortDay(bannerReturnDay(entry));
  const hideLabel = `Hide until ${back.replace(/^on /, "")}`;

  return (
    <div className={cn("flex items-center gap-3 rounded-2xl px-3 py-2.5 sm:px-4", tone.box)}>
      <div className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-lg text-center leading-none", tone.badge)}>
        {days === null ? (
          <span className="text-[13px] font-semibold">{item.kind === "oa" ? "OA" : "Int"}</span>
        ) : days === 0 ? (
          <span className="text-[11.5px] font-semibold">Today</span>
        ) : (
          <span>
            <span className="block text-[17px] font-semibold">{days}</span>
            <span className="mt-px block text-[9.5px] opacity-80">{days === 1 ? "day" : "days"}</span>
          </span>
        )}
      </div>
      <button type="button" onClick={onOpen} title="Open in Applications" className="group min-w-0 flex-1 text-left">
        <p className="truncate text-[14.5px] font-semibold decoration-current/40 underline-offset-[3px] group-hover:underline">
          {attentionTitle(item)}
        </p>
        {sub && <p className={cn("truncate text-[12.5px]", tone.sub)}>{sub}</p>}
      </button>
      {!!more && (
        <button
          type="button"
          onClick={onToggleMore}
          aria-expanded={expanded}
          className={cn("shrink-0 rounded-full px-2.5 py-1 text-[12.5px] font-medium transition-colors", tone.icon)}
        >
          {expanded ? "Show less" : `+${more} more`}
        </button>
      )}
      <button
        type="button"
        onClick={onPrep ? onPrep.go : onDone}
        className={cn(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] px-3.5 text-[13px] font-semibold transition-opacity hover:opacity-90",
          tone.done,
        )}
      >
        {onPrep ? (
          <>
            {onPrep.label} <ArrowRight size={14} strokeWidth={2} />
          </>
        ) : (
          <>
            <Check size={14} strokeWidth={2} /> Done
          </>
        )}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="More"
            title="More"
            className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-[10px] transition-colors data-[state=open]:bg-black/5", tone.icon)}
          >
            <MoreHorizontal size={18} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-52 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
        >
          {onPrep && (
            <DropdownMenuItem onSelect={onDone} className={MENU_ITEM}>
              <Check size={15} strokeWidth={1.75} /> Mark as done
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={onOpen} className={MENU_ITEM}>
            <ArrowUpRight size={15} strokeWidth={1.75} /> Open in Applications
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-melt-hair" />
          <DropdownMenuItem onSelect={onHide} className={MENU_ITEM}>
            <EyeOff size={15} strokeWidth={1.75} /> {hideLabel}
          </DropdownMenuItem>
          <p className="px-2.5 pb-1.5 text-[11.5px] leading-snug text-melt-text3">It stays in your to-dos.</p>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** "tomorrow", "Friday", "Oct 14" for a yyyy-mm-dd */
function shortDay(day: string) {
  const days = daysUntil(day) ?? 0;
  const at = new Date(day + "T00:00:00");
  if (days <= 1) return "tomorrow";
  if (days < 7) return `on ${at.toLocaleDateString("en-US", { weekday: "long" })}`;
  return `on ${at.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/** The soonest OA or interview, with "+N more" stacking the rest under it. */
function AttentionBanner({
  banner,
  expanded,
  onToggle,
  onDone,
  onOpen,
  onHide,
  onPrep,
}: {
  banner: Banner;
  expanded: boolean;
  onToggle: () => void;
  onDone: (entry: BannerEntry) => void;
  onOpen: (entry: BannerEntry) => void;
  onHide: (entry: BannerEntry) => void;
  /** Undefined when this entry has no prep */
  onPrep: (entry: BannerEntry) => BannerPrep | undefined;
}) {
  const row = (entry: BannerEntry, first: boolean) => (
    <AttentionRow
      key={entry.todo.id}
      entry={entry}
      more={first ? banner.more : 0}
      expanded={expanded}
      onToggleMore={onToggle}
      onDone={() => onDone(entry)}
      onOpen={() => onOpen(entry)}
      onHide={() => onHide(entry)}
      onPrep={onPrep(entry)}
    />
  );
  return (
    <div className="flex flex-col gap-2">
      {row(banner, true)}
      {expanded && banner.rest.map((entry) => row(entry, false))}
    </div>
  );
}

/** While a new user's first Gmail sync runs, in place of an empty card. */
function FirstSyncNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 flex items-start gap-2.5 text-[13.5px] leading-relaxed text-melt-text2">
      <span className="mt-[7px] h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-melt-caramel" />
      <p>{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------- since you were here

/** One line under the greeting: what changed since the last visit. */
function SinceStrip({ since, items }: { since: number; items: string[] }) {
  const label = sinceLabel(since);
  // Nothing new: no line at all
  if (!items.length) return null;
  const text = items.join(", ");
  return (
    <p
      className="mt-1.5 flex min-w-0 items-center gap-2 text-[13.5px] text-melt-text2"
      title={`Since ${label}: ${text}`}
    >
      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-melt-caramel" />
      <span className="truncate">
        <span className="font-medium text-melt-text1">Since {label}:</span> {text}
      </span>
    </p>
  );
}

// ---------------------------------------------------------------- to-dos

const AUTO_OPEN_LABELS: Record<AutoPage, string> = {
  tracker: "Open in Applications",
  dream: "Open in Dream Companies",
  prep: "Open in Interview Prep",
  roles: "Open in New Roles",
  drafts: "Open in Drafts",
  goal: "Set your weekly goal",
  feedback: "Send feedback",
};

/** The weekly goal, for a new user who has no Your season row to set it on yet. */
function GoalDialog({ open, goal, onSave, onClose }: { open: boolean; goal: number; onSave: (goal: number) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(String(goal));
  const n = Math.round(Number(draft));
  const valid = n >= 1 && n <= 100;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
        <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">
          Set your weekly goal
        </DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
          How many jobs do you want to apply to each week?
        </DialogDescription>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            onSave(n);
            onClose();
          }}
        >
          <label className="mt-4 flex items-center gap-2 text-[13.5px] text-melt-text2">
            <input
              type="number"
              min={1}
              max={100}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              aria-label="Applications a week"
              className="h-10 w-20 rounded-lg border border-melt-hair bg-white px-3 text-[15px] text-melt-text1 outline-none focus:border-melt-caramel"
            />
            apps a week
          </label>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className={MELT_BUTTON_QUIET}>
              Cancel
            </button>
            <button type="submit" disabled={!valid} className={cn(MELT_BUTTON, "disabled:opacity-50")}>
              Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({
  task,
  onToggle,
  onEdit,
  onOpen,
  onSnooze,
  onDelete,
}: {
  task: Task;
  onToggle: () => void;
  /** A yyyy-mm-dd day to snooze it to, or null to stop snoozing */
  onSnooze: (until: string | null) => void;
  /** Your own to-dos: the title edits it */
  onEdit?: () => void;
  /** Caramel's to-dos: the link opens the card or company it's about */
  onOpen?: () => void;
  /** Deletes your own to-do, or dismisses one of Caramel's */
  onDelete: () => void;
}) {
  const due = dueLabel(task.dueDate);
  const openLabel = AUTO_OPEN_LABELS[(task.auto && autoTarget(task)) || "tracker"];
  return (
    <div className="group flex min-h-[44px] items-center gap-3 border-b border-melt-line/70 py-2 last:border-b-0">
      <input
        type="checkbox"
        checked={task.completed}
        onChange={onToggle}
        aria-label={task.completed ? `Mark “${task.title}” not done` : `Mark “${task.title}” done`}
        className="h-[18px] w-[18px] shrink-0 cursor-pointer rounded-[5px] border-melt-hair accent-melt-caramel"
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1">
          {onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className={cn(
                "min-w-0 truncate text-left text-[14px] font-medium text-melt-text1",
                task.completed && "text-melt-text3 line-through",
              )}
            >
              {task.title}
            </button>
          ) : task.auto?.kind === "setup" && onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className={cn(
                "min-w-0 truncate text-left text-[14px] font-medium text-melt-text1",
                task.completed && "text-melt-text3 line-through",
              )}
            >
              {task.title}
            </button>
          ) : (
            <span
              className={cn(
                "min-w-0 truncate text-[14px] font-medium text-melt-text1",
                task.completed && "text-melt-text3 line-through",
              )}
            >
              {task.title}
            </span>
          )}
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              aria-label={openLabel}
              title={openLabel}
              className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
            >
              <ArrowUpRight size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>
        {/* Caramel's to-dos say why they're here */}
        {task.reason && !task.completed && (
          <p className="truncate text-[12.5px] leading-snug text-melt-text3" title={task.reason}>
            {task.reason}
          </p>
        )}
      </div>
      {due && !task.completed && (
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 font-plex text-xs",
            due === "Overdue" ? "text-[#A5492E]" : "text-melt-text3",
          )}
          title={task.snoozed ? "Snoozed" : undefined}
        >
          {task.snoozed && <AlarmClock size={12} strokeWidth={1.75} aria-label="Snoozed" />}
          {due}
        </span>
      )}
      {!task.completed && <SnoozeMenu task={task} onSnooze={onSnooze} />}
      <button
        type="button"
        onClick={onDelete}
        aria-label={task.auto && !task.completed ? `Dismiss “${task.title}”` : `Delete “${task.title}”`}
        title={task.auto && !task.completed ? "Dismiss" : "Delete"}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-melt-text3 opacity-0 transition-opacity hover:bg-melt-text1/[.06] hover:text-[#A5492E] focus:opacity-100 group-hover:opacity-100"
      >
        <X size={15} strokeWidth={1.75} />
      </button>
    </div>
  );
}

/** The clock on a to-do row: snooze it to a later day, or stop snoozing. */
function SnoozeMenu({ task, onSnooze }: { task: Task; onSnooze: (until: string | null) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Snooze “${task.title}”`}
          title="Snooze"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-melt-text3 opacity-0 transition-opacity hover:bg-melt-text1/[.06] hover:text-melt-text1 focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <AlarmClock size={15} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-44 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        <p className="px-2.5 pb-1 pt-1.5 font-plex text-[10.5px] uppercase tracking-[.08em] text-melt-text3">
          Snooze until
        </p>
        {snoozeOptions().map((option) => (
          <DropdownMenuItem key={option.date} onSelect={() => onSnooze(option.date)} className={MENU_ITEM}>
            {option.label}
          </DropdownMenuItem>
        ))}
        {task.snoozed && (
          <>
            <DropdownMenuSeparator className="bg-melt-hair" />
            <DropdownMenuItem onSelect={() => onSnooze(null)} className={MENU_ITEM}>
              Stop snoozing
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The row for adding a to-do, or editing one in place (`task`). */
function TaskForm({
  task,
  onSubmit,
  onCancel,
}: {
  task?: Task;
  onSubmit: (title: string, dueDate: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [dueDate, setDueDate] = useState(task?.dueDate ?? "");
  const formRef = useRef<HTMLFormElement>(null);

  // Clicking anywhere else drops the row without saving (the calendar opens outside the form, so it counts as inside)
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (formRef.current?.contains(target) || target?.closest("[data-date-picker]")) return;
      onCancel();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [onCancel]);

  const submit = () => {
    if (!title.trim()) return onCancel();
    onSubmit(title.trim(), dueDate);
    if (task) return;
    // Adding stays open for the next one
    setTitle("");
    setDueDate("");
  };
  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex items-center gap-2 border-b border-melt-line/70 py-2"
    >
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
        placeholder="What do you need to do?"
        className="min-w-0 flex-1 bg-transparent text-[14px] text-melt-text1 outline-none placeholder:text-melt-text3"
      />
      <DatePicker
        value={dueDate}
        onChange={setDueDate}
        placeholder="Due date"
        aria-label="Due date (optional)"
        className="w-[124px] rounded-md border border-melt-hair bg-white px-2 py-1 font-plex text-xs text-melt-text2 outline-none focus:border-melt-caramel"
      />
      <button
        type="submit"
        className="rounded-md bg-melt-caramel px-3 py-1 text-[13px] font-medium text-white hover:bg-melt-press"
      >
        {task ? "Save" : "Add"}
      </button>
    </form>
  );
}

/** users/{uid}/autoTodos/{key}: keys hold ":" and ids from other docs, so they're encoded */
const autoTodoRef = (user: User, key: string) => doc(db, "users", user.uid, "autoTodos", encodeURIComponent(key));

/** Saves what the user did with one of Caramel's to-dos (merged into its state). */
type SaveAuto = (task: Task, change: Record<string, unknown>) => void;

/** Firestore timestamp or ms -> ms, for ordering Done */
const doneAt = (v: any): number => (typeof v === "number" ? v : typeof v?.toMillis === "function" ? v.toMillis() : 0);

function TasksCard({
  user,
  autoTasks,
  saveAuto,
  onOpenAuto,
  firstSync,
}: {
  user: User | null;
  /** The first Gmail sync is running and nothing is in yet */
  firstSync: boolean;
  /** Caramel's to-dos, with what the user did applied (not the one on the banner) */
  autoTasks: Task[];
  saveAuto: SaveAuto;
  onOpenAuto: (task: Task) => void;
}) {
  const [ownTasks, setOwnTasks] = useState<Task[]>([]);
  // Until the first snapshot, so a refresh doesn't flash "No to-dos yet"
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TaskTab>("today");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAllDone, setShowAllDone] = useState(false);
  const [showAllToday, setShowAllToday] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const openedAt = useRef(Date.now());
  const day = localDay(new Date().toISOString());
  const { toast, show: showToast, hide: hideToast } = useUndoToast();

  useEffect(() => {
    if (!user) {
      setOwnTasks([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "todos"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setOwnTasks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Task));
        setLoading(false);
      },
      (error) => {
        console.error("Could not load to-dos:", error);
        setLoading(false);
      },
    );
  }, [user]);

  const tasks = useMemo(
    () => [...ownTasks.map((t) => applySnooze(t, t.snoozedUntil)), ...autoTasks],
    [ownTasks, autoTasks],
  );

  const byTab = useMemo(() => {
    const groups: Record<TaskTab, Task[]> = {
      today: [],
      week: [],
      later: [],
      done: [],
    };
    tasks.forEach((task) => groups[taskTab(task)].push(task));
    (["today", "week", "later"] as TaskTab[]).forEach((k) => {
      groups[k] = tickedLast(groups[k].sort(compareTasks), openedAt.current);
    });
    groups.done.sort((a, b) => doneAt(b.completedAt ?? b.createdAt) - doneAt(a.completedAt ?? a.createdAt));
    return groups;
    // day: ticked ones move to Done at midnight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, day]);

  const ref = (id: string) => (user ? doc(db, "users", user.uid, "todos", id) : null);
  const addTask = async (title: string, dueDate: string) => {
    if (!user) return;
    try {
      // priority keeps the shape older to-dos have
      await addDoc(collection(db, "users", user.uid, "todos"), {
        title,
        dueDate,
        priority: "medium",
        completed: false,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error adding task:", error);
      alert("Failed to add task");
    }
  };

  const editTask = (task: Task, title: string, dueDate: string) => {
    setEditingId(null);
    const r = ref(task.id);
    // A date picked by hand replaces any snooze
    if (r)
      updateDoc(r, { title, dueDate, snoozedUntil: deleteField() }).catch((e) =>
        console.error("Error updating task:", e),
      );
  };

  const toggleTask = (task: Task) => {
    // Unticking one of Caramel's brings it back if it still applies
    if (task.auto)
      return saveAuto(task, task.completed ? { status: deleteField() } : { status: "done", hidden: false });
    const r = ref(task.id);
    if (r)
      updateDoc(r, { completed: !task.completed, completedAt: task.completed ? deleteField() : serverTimestamp() }).catch(
        (e) => console.error("Error updating task:", e),
      );
  };

  const snoozeTask = (task: Task, until: string | null) => {
    const snoozedUntil = until ?? deleteField();
    // A snoozed follow-up gets its full days in the list again when it's back
    if (task.auto) return saveAuto(task, task.auto.kind === "followup" ? { snoozedUntil, shownOn: deleteField() } : { snoozedUntil });
    const r = ref(task.id);
    if (r) updateDoc(r, { snoozedUntil }).catch((e) => console.error("Error snoozing task:", e));
  };

  const deleteTask = (task: Task) => {
    // Kept as dismissed (or hidden from Done) so it doesn't come back
    if (task.auto) {
      saveAuto(task, task.completed ? { hidden: true } : { status: "dismissed" });
      showToast({
        title: task.completed ? "To-do deleted" : "To-do dismissed",
        sub: task.title,
        undo: () => {
          hideToast();
          // Its old tick time, so an old one goes back to Done
          saveAuto(
            task,
            task.completed ? { hidden: false, ...(task.completedAt ? { at: task.completedAt } : {}) } : { status: deleteField() },
          );
        },
      });
      return;
    }
    const r = ref(task.id);
    if (!r) return;
    // As stored: the list's copy has any snooze applied to its date
    const { id: _id, ...stored } = ownTasks.find((t) => t.id === task.id) ?? task;
    deleteDoc(r)
      .then(() =>
        showToast({
          title: "To-do deleted",
          sub: task.title,
          undo: () => {
            hideToast();
            setDoc(r, stored).catch((e) => console.error("Error restoring task:", e));
          },
        }),
      )
      .catch((e) => console.error("Error deleting task:", e));
  };

  const clearDone = async () => {
    setConfirmClear(false);
    if (!user || !byTab.done.length) return;
    try {
      const batch = writeBatch(db);
      byTab.done.forEach((task) =>
        task.auto
          ? batch.set(autoTodoRef(user, task.id), { hidden: true }, { merge: true })
          : batch.delete(doc(db, "users", user.uid, "todos", task.id)),
      );
      await batch.commit();
      setShowAllDone(false);
    } catch (error) {
      console.error("Error clearing done tasks:", error);
      alert("Failed to clear done tasks");
    }
  };

  const isDone = tab === "done";
  const limitToday = todayLimit(byTab.today);
  // What's left to do; Done counts everything in it
  const openCount = Object.fromEntries(
    TASK_TABS.map(({ id }) => [id, byTab[id].filter((t) => id === "done" || !t.completed).length]),
  ) as Record<TaskTab, number>;
  // Done keeps growing, so it shows the latest few; "See more" scrolls the rest
  // Today keeps to the few that matter most; the rest are a click away
  const todayRows = withinLimit(byTab.today, limitToday);
  const shown =
    isDone && !showAllDone
      ? byTab.done.slice(0, DONE_PREVIEW)
      : tab === "today" && !showAllToday
        ? todayRows
        : byTab[tab];
  const hiddenToday = tab === "today" ? byTab.today.length - todayRows.length : 0;

  return (
    <section className={cn(CARD, "p-4 sm:p-5")}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-melt-text1">To-do</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-[10px] bg-melt-text1/[.05] p-1" role="tablist">
            {TASK_TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  "rounded-[7px] px-3 py-1 text-[13px] font-medium transition-colors",
                  tab === id
                    ? "bg-white text-melt-text1 shadow-[0_1px_2px_rgba(92,52,23,.08)]"
                    : "text-melt-text2 hover:text-melt-text1",
                )}
              >
                {label}{" "}
                {!loading && <span className="ml-0.5 font-plex text-[11px] text-melt-text3">{openCount[id]}</span>}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
              if (tab === "done") setTab("today");
            }}
            aria-label="Add a to-do"
            title="Add a to-do"
            className="grid h-6 w-6 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
          >
            <Plus size={16} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className={cn("mt-3", isDone && showAllDone && "max-h-[340px] overflow-y-auto pr-1")}>
        {adding && <TaskForm onSubmit={addTask} onCancel={() => setAdding(false)} />}
        {shown.map((task) =>
          editingId === task.id ? (
            <TaskForm
              key={task.id}
              task={task}
              onSubmit={(title, dueDate) => editTask(task, title, dueDate)}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <TaskRow
              key={task.id}
              task={task}
              onToggle={() => toggleTask(task)}
              onSnooze={(until) => snoozeTask(task, until)}
              onEdit={
                task.auto
                  ? undefined
                  : () => {
                      setAdding(false);
                      setEditingId(task.id);
                    }
              }
              // Done ones that no longer apply have nowhere to go
              onOpen={autoTarget(task) ? () => onOpenAuto(task) : undefined}
              onDelete={() => deleteTask(task)}
            />
          ),
        )}
        {loading && !adding && (
          <div className="flex items-center justify-center py-6">
            <div className="text-[13.5px] text-melt-text3">Loading...</div>
          </div>
        )}
        {!loading && !adding && tasks.length === 0 && firstSync ? (
          <FirstSyncNote>
            Reading your Gmail for applications. Caramel writes your first to-dos from what it finds, so this fills in
            on its own. The first time takes a minute or two.
          </FirstSyncNote>
        ) : (
          !loading &&
          !adding &&
          shown.length === 0 && (
            <p className="py-3 text-[13.5px] text-melt-text3">
              {tab === "done"
                ? "Nothing finished yet."
                : tasks.length === 0
                  ? "No to-dos yet. Add one to keep track of what’s next."
                  : "Nothing here."}
            </p>
          )
        )}
      </div>

      {hiddenToday > 0 && (
        <button
          type="button"
          onClick={() => setShowAllToday((v) => !v)}
          className="mt-2 text-[13px] font-medium text-melt-text2 hover:text-melt-text1"
        >
          {showAllToday ? "Show less" : `Show ${hiddenToday} more`}
        </button>
      )}

      {isDone && byTab.done.length > 0 && (
        <div className="mt-2 flex items-center justify-between gap-3">
          {byTab.done.length > DONE_PREVIEW ? (
            <button
              type="button"
              onClick={() => setShowAllDone((v) => !v)}
              className="text-[13px] font-medium text-melt-text2 hover:text-melt-text1"
            >
              {showAllDone ? "Show less" : `See more (${byTab.done.length - DONE_PREVIEW})`}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            className="text-[13px] font-medium text-melt-text3 hover:text-[#A5492E]"
          >
            Clear all
          </button>
        </div>
      )}

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
          <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">
            Clear done to-dos?
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
            {plural(byTab.done.length, "finished to-do")} will be deleted. This can’t be undone.
          </DialogDescription>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmClear(false)} className={MELT_BUTTON_QUIET}>
              Cancel
            </button>
            <button
              type="button"
              onClick={clearDone}
              className="inline-flex h-11 items-center rounded-xl bg-[#A5492E] px-5 text-[15px] font-medium text-white transition-opacity hover:opacity-90"
            >
              Clear all
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <UndoToast toast={toast} />
    </section>
  );
}

// ---------------------------------------------------------------- watchlist

function companyStatus(company: DreamCompany): {
  text: string;
  fresh: boolean;
} {
  const unseen = unseenNewRoles(company).length;
  if (unseen) return { text: `${plural(unseen, "new role")} this week`, fresh: true };
  const state = trackingState(company);
  if (state === "pending") return { text: "Finding their job board…", fresh: false };
  if (state === "untracked") return { text: "Can’t follow their job board", fresh: false };
  const open = openRoles(company).length;
  return open
    ? { text: `${plural(open, "open role")} for you`, fresh: false }
    : { text: "No openings. We’ll let you know.", fresh: false };
}

/** The newest role opened since the user last looked, across all companies. */
function newestRole(companies: DreamCompany[]): { company: DreamCompany; role: OpenRole } | null {
  let best: { company: DreamCompany; role: OpenRole; at: number } | null = null;
  for (const company of companies) {
    for (const role of unseenNewRoles(company)) {
      const at = Date.parse(role.firstSeenAt || "") || 0;
      if (!best || at > best.at) best = { company, role, at };
    }
  }
  return best;
}

const WATCHLIST_ROWS = 4;

function WatchlistCard({
  companies,
  loading,
  onOpen,
  onOpenCompany,
}: {
  companies: DreamCompany[];
  loading: boolean;
  onOpen: () => void;
  onOpenCompany: (id: string) => void;
}) {
  const sorted = useMemo(() => [...companies].sort((a, b) => compareDreamCompanies(a, b)), [companies]);
  const highlight = newestRole(companies);

  return (
    <section className={cn(CARD, "p-4 sm:p-5")}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-melt-text1">Watchlist</h2>
        <button type="button" onClick={onOpen} className="text-[12.5px] text-melt-text3 hover:text-melt-text1">
          from Dream Companies
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="text-[13.5px] text-melt-text3">Loading...</div>
        </div>
      ) : companies.length === 0 ? (
        <div className="mt-3 text-[13.5px] leading-relaxed text-melt-text2">
          Follow the places you’d love to work and new roles show up here.
          <button
            type="button"
            onClick={onOpen}
            className="mt-3 block font-medium text-melt-text1 underline underline-offset-2"
          >
            Add dream companies
          </button>
        </div>
      ) : (
        <>
          {highlight && (
            <div className="mt-3 rounded-xl bg-melt-peach px-3.5 py-3">
              <p className="font-plex text-[10.5px] uppercase tracking-[.08em] text-melt-amber">New role opened</p>
              <p className="mt-1 text-[14px] font-semibold text-melt-text1">
                {highlight.company.name} · {highlight.role.title}
              </p>
              <a
                href={highlight.role.url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-[13px] text-melt-text1 underline underline-offset-2"
              >
                View role
              </a>
            </div>
          )}
          <ul className="mt-2">
            {sorted.slice(0, WATCHLIST_ROWS).map((company) => {
              const status = companyStatus(company);
              return (
                <li key={company.id}>
                  <button
                    type="button"
                    onClick={() => onOpenCompany(company.id)}
                    className="flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-melt-text1/[.04]"
                  >
                    <CompanyLogo name={company.name} domain={company.domain} size={32} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-medium text-melt-text1">{company.name}</span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-melt-text3">
                        <span
                          className={cn(
                            "h-1.5 w-1.5 shrink-0 rounded-full",
                            status.fresh ? "bg-melt-caramel" : "bg-melt-oat",
                          )}
                        />
                        <span className="truncate">{status.text}</span>
                      </span>
                    </span>
                    <ChevronRight size={16} strokeWidth={1.75} className="shrink-0 text-melt-text3" />
                  </button>
                </li>
              );
            })}
          </ul>
          {companies.length > WATCHLIST_ROWS && (
            <button
              type="button"
              onClick={onOpen}
              className="mt-1 px-1 text-[12.5px] text-melt-text3 hover:text-melt-text1"
            >
              +{companies.length - WATCHLIST_ROWS} more
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- season

const FUNNEL: { id: FunnelStep; label: string; color: string }[] = [
  { id: "applied", label: "Applied", color: "#C7B8A8" },
  { id: "oa", label: "Assessment", color: "#E2B884" },
  { id: "interview", label: "Interview", color: "#CF8C3F" },
  { id: "offer", label: "Offer", color: "#6E9C86" },
];

const OUTCOMES: { id: FunnelStep; label: string; color: string }[] = [
  { id: "rejected", label: "Rejected", color: "#C98A72" },
  { id: "ghosted", label: "Ghosted", color: "#D9CFC6" },
];

/** Label, bar, number: every row in Your season, the weekly goal included */
const STATS_ROW = "grid grid-cols-[88px_minmax(0,1fr)_44px] items-center gap-3";

function FunnelRow({ label, color, n, total }: { label: string; color: string; n: number; total: number }) {
  return (
    <li className={STATS_ROW}>
      <span className="text-[13.5px] text-melt-text2">{label}</span>
      <span className="h-[18px] overflow-hidden rounded-md bg-melt-text1/[.05]">
        {n > 0 && (
          <span
            className="block h-full rounded-md"
            // Small counts keep a sliver so they read as "some", not "none"
            style={{
              width: `max(12px, ${(n / total) * 100}%)`,
              background: color,
            }}
          />
        )}
      </span>
      <span className="text-right text-[14px] font-semibold tabular-nums text-melt-text1">{n}</span>
    </li>
  );
}

/** The week's application goal as a stats row: "This week [bar] 3 / 10", pencil on hover to change it. */
function GoalRow({ week, onSetGoal }: { week: WeekGoal; onSetGoal: (goal: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(week.goal));
  const save = () => {
    const n = Math.round(Number(draft));
    if (n >= 1 && n <= 100) onSetGoal(n);
    setEditing(false);
  };
  if (editing)
    return (
      <form
        className="flex items-center gap-2 text-[13.5px] text-melt-text2"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        Goal
        <input
          type="number"
          min={1}
          max={100}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
          autoFocus
          aria-label="Applications a week"
          className="h-[22px] w-14 rounded-md border border-melt-hair bg-white px-2 text-[13px] text-melt-text1 outline-none focus:border-melt-caramel"
        />
        apps a week
        <button type="submit" className="ml-auto text-[13px] font-medium text-melt-text1 hover:underline">
          Save
        </button>
      </form>
    );
  return (
    <div className={cn(STATS_ROW, "group")}>
      <span className="flex items-center gap-0.5 whitespace-nowrap text-[13.5px] text-melt-text2">
        This week
        <button
          type="button"
          onClick={() => {
            setDraft(String(week.goal));
            setEditing(true);
          }}
          aria-label="Change your weekly goal"
          title="Change your weekly goal"
          className="grid h-4 w-4 shrink-0 place-items-center rounded text-melt-text3 opacity-0 transition-opacity hover:text-melt-text1 focus:opacity-100 group-hover:opacity-100"
        >
          <Pencil size={12} strokeWidth={1.75} />
        </button>
      </span>
      <span className="h-[18px] overflow-hidden rounded-md bg-melt-text1/[.05]">
        {week.done > 0 && (
          <span
            className="block h-full rounded-md transition-[width] duration-300"
            // Its own warm gradient: the one bar to chase, distinct from the funnel's flat colors
            style={{
              width: `max(12px, ${Math.min(1, week.done / week.goal) * 100}%)`,
              background: "linear-gradient(90deg, #E6A45C, #D98829)",
            }}
          />
        )}
      </span>
      <span className="whitespace-nowrap text-right text-[14px] font-semibold tabular-nums text-melt-text1">
        {week.done} <span className="font-normal text-melt-text3">/ {week.goal}</span>
      </span>
    </div>
  );
}

function SeasonCard({
  cards,
  loading,
  firstSync,
  weeklyGoalSetting,
  onSetGoal,
  onOpen,
}: {
  cards: ApiItem[];
  loading: boolean;
  /** The first Gmail sync is running and nothing is in yet */
  firstSync: boolean;
  /** The user's applications-a-week goal, if they set one */
  weeklyGoalSetting: number | undefined;
  onSetGoal: (goal: number) => void;
  onOpen: () => void;
}) {
  const summary = useMemo(() => seasonSummary(cards), [cards]);
  const { season, counts, slip } = summary;
  const week = weeklyGoal(cards, weeklyGoalSetting || DEFAULT_WEEKLY_GOAL);
  // Under 50 applications the advice is to apply more, tied to the goal; from 50 on, where it's slipping
  const advice = counts.applied < APPLY_TARGET ? keepApplying(summary, week) : slip;

  return (
    <section className={cn(CARD, "p-4 sm:p-5")}>
      <div className="flex items-baseline justify-between gap-3 border-b border-melt-line pb-3">
        <h2 className="text-[16px] font-semibold text-melt-text1">Your season</h2>
        {season && <span className="truncate font-plex text-[12.5px] text-melt-text3">{season}</span>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="text-[13.5px] text-melt-text3">Loading...</div>
        </div>
      ) : counts.applied === 0 && firstSync ? (
        <FirstSyncNote>Finding your applications in Gmail. Your season fills in as they come in.</FirstSyncNote>
      ) : counts.applied === 0 ? (
        <div className="mt-3 text-[13.5px] leading-relaxed text-melt-text2">
          Once you apply, you’ll see how far your applications get here.
          <button
            type="button"
            onClick={onOpen}
            className="mt-3 block font-medium text-melt-text1 underline underline-offset-2"
          >
            Go to applications
          </button>
        </div>
      ) : (
        <>
          <div className="mt-3 border-b border-melt-line pb-3">
            <GoalRow week={week} onSetGoal={onSetGoal} />
          </div>
          <ul className="mt-3 flex flex-col gap-2.5">
            {FUNNEL.map((step) => (
              <FunnelRow key={step.id} {...step} n={counts[step.id]} total={counts.applied} />
            ))}
          </ul>
          <ul className="mt-3 flex flex-col gap-2.5 border-t border-melt-line pt-3">
            {OUTCOMES.map((step) => (
              <FunnelRow key={step.id} {...step} n={counts[step.id]} total={counts.applied} />
            ))}
          </ul>
          {advice && (
            <div className="mt-4 rounded-xl border border-[#EBD5B5] bg-melt-peach px-3.5 py-3">
              <p className="text-[13.5px] font-semibold text-melt-amber">
                {advice.title}
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-melt-text2">{advice.body}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- page

export default function Home() {
  const { setNavbarActions, userRecord, userRecordLoading } = useNavbar();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  // On a refresh Firebase doesn't know the user yet; hold the greeting so it doesn't flash a nameless one
  const [authReady, setAuthReady] = useState(auth.currentUser !== null);
  const [cards, setCards] = useState<ApiItem[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [companies, setCompanies] = useState<DreamCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  // What the user did with Caramel's to-dos, by key
  const [autoState, setAutoState] = useState<Record<string, AutoTodoState>>({});
  // Until the first snapshot every auto to-do looks untouched, so nothing ticks itself before then
  const [autoLoaded, setAutoLoaded] = useState(false);
  // Re-render each minute so "synced 4 min ago" and deadlines stay current
  const [minuteTick, setMinuteTick] = useState(0);

  useEffect(() => {
    const tick = window.setInterval(() => setMinuteTick((n) => n + 1), 60000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        setAuthReady(true);
      }),
    [],
  );

  // Current-cycle tracker cards, for OA / interview deadlines and the season funnel
  useEffect(() => {
    if (!user) {
      setCards([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "emails"), where("archiveId", "==", null));
    return onSnapshot(
      q,
      (snapshot) => {
        setCards(toApplications(snapshot.docs));
        setCardsLoading(false);
      },
      (error) => {
        console.error("Could not load applications:", error);
        setCardsLoading(false);
      },
    );
  }, [user]);

  // Dream companies, for the watchlist
  useEffect(() => {
    if (!user) {
      setCompanies([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "dreamCompanies"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setCompanies(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as DreamCompany));
        setCompaniesLoading(false);
      },
      (error) => {
        console.error("Could not load dream companies:", error);
        setCompaniesLoading(false);
      },
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setAutoState({});
      setAutoLoaded(false);
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "autoTodos"),
      (snapshot) => {
        const state: Record<string, AutoTodoState> = {};
        snapshot.docs.forEach((d) => (state[decodeURIComponent(d.id)] = d.data() as AutoTodoState));
        setAutoState(state);
        setAutoLoaded(true);
      },
      (error) => console.error("Could not load auto to-dos:", error),
    );
  }, [user]);

  // Today's Interview Prep, as one to-do
  const prep = usePrepPlan(user);
  const dailyPrep = useMemo(
    () =>
      prep.plan ? dailyPrepTodo(prep.plan[prep.today] ?? [], (id) => prep.stateById.get(id)?.status, prep.today) : null,
    [prep.plan, prep.stateById, prep.today],
  );

  const joinedAt = doneAt(userRecord?.onboardingCompletedAt);
  const autoTasks = useMemo(
    () =>
      limitFollowUps(
        withAutoState(
          [
            ...setupTodos(companies, userRecord?.starterTodos === true, userRecord?.exploreTodos === true),
            ...(dailyPrep ? [dailyPrep] : []),
            ...autoTodos(cards, companies, new Date(), joinedAt),
          ],
          autoState,
        ),
        autoState,
      ),
    // minuteTick: deadlines that pass move on
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, companies, autoState, userRecord?.starterTodos, userRecord?.exploreTodos, dailyPrep, joinedAt, minuteTick],
  );
  const banner = useMemo(
    () => pickBanner(cards, autoTasks, autoState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, autoTasks, autoState, minuteTick],
  );
  const [bannerExpanded, setBannerExpanded] = useState(false);
  // The banner's to-dos aren't in the list as well
  const listedAuto = useMemo(() => {
    if (!banner) return autoTasks;
    const shown = new Set([banner, ...(bannerExpanded ? banner.rest : [])].map((e) => e.todo.id));
    return autoTasks.filter((t) => !shown.has(t.id));
  }, [autoTasks, banner, bannerExpanded]);

  const saveAuto: SaveAuto = (task, change) => {
    if (!user) return;
    setDoc(autoTodoRef(user, task.id), { title: task.title, at: serverTimestamp(), ...change }, { merge: true }).catch(
      (e) => console.error("Error updating auto to-do:", e),
    );
  };

  // The day a follow-up first shows, so it goes on its own a few days later (limitFollowUps)
  const unstampedFollowUp = autoLoaded
    ? autoTasks.find((t) => t.auto?.kind === "followup" && !t.completed && !t.snoozed && !autoState[t.id]?.shownOn)
    : undefined;
  useEffect(() => {
    if (unstampedFollowUp) saveAuto(unstampedFollowUp, { shownOn: localDay(new Date().toISOString()) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unstampedFollowUp?.id]);

  // "Save a job" is done once a job is saved: from New roles, or by link or by hand in Drafts
  const saveJobTodo = autoTasks.find((t) => t.auto?.page === "drafts" && !t.completed);
  const waitingForSave = autoLoaded && !!saveJobTodo;
  useEffect(() => {
    if (!user || !waitingForSave) return;
    const saved = (snapshot: QuerySnapshot) => {
      if (!snapshot.empty && saveJobTodo) saveAuto(saveJobTodo, { status: "done", hidden: false });
    };
    const logError = (e: Error) => console.error("Could not check saved jobs:", e);
    const stops = [
      onSnapshot(query(collection(db, "users", user.uid, "roleActions"), where("status", "==", "saved"), limit(1)), saved, logError),
      onSnapshot(query(collection(db, "users", user.uid, "writtenApplications"), limit(1)), saved, logError),
    ];
    return () => stops.forEach((stop) => stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, waitingForSave]);

  // Syncs Gmail when it's been over an hour (utils/gmailSync.ts)
  const { times, needsReconnect } = useAutoSync(user);
  const syncing = useSyncing();
  const lastSync = times?.lastSyncAt ?? 0;
  // A new user's first sync: never synced, running now, nothing in yet
  const firstSync = syncing && !lastSync && !cardsLoading && cards.length === 0;

  // On the tour, following three dream companies ticks that to-do
  const dreamTodo = autoTasks.find((t) => t.auto?.page === "dream" && !t.completed);
  const followsEnough = companies.length >= STARTER_DREAM_COMPANIES;
  useEffect(() => {
    if (autoLoaded && followsEnough && dreamTodo) saveAuto(dreamTodo, { status: "done", hidden: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoaded, followsEnough, !!dreamTodo]);

  const [goalOpen, setGoalOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);
  const setGoal = (goal: number) => {
    if (!user) return;
    updateDoc(doc(db, "users", user.uid), { weeklyGoal: goal }).catch((e) =>
      console.error("Could not save weekly goal:", e),
    );
    const todo = autoTasks.find((t) => t.auto?.page === "goal" && !t.completed);
    if (todo) saveAuto(todo, { status: "done", hidden: false });
  };

  // An auto to-do opens the card or company it's about
  const openAuto = (task: Task) => {
    const { cardId, companyId } = task.auto || {};
    const target = autoTarget(task);
    if (opensToDone(task) && !task.completed) saveAuto(task, { status: "done", hidden: false });
    if (target === "goal") setGoalOpen(true);
    else if (target === "feedback") setFeedbackOpen(true);
    else if (target === "prep") navigate("/dashboard/prep");
    else if (target === "roles") navigate("/dashboard/roles");
    // Saving a job: New roles has some ready to save, Drafts starts empty
    else if (target === "drafts") navigate("/dashboard/roles");
    else if (target === "dream")
      navigate("/dashboard/dreamcompanies", { state: companyId ? { openCompany: companyId } : null });
    else navigate("/dashboard/tracker", { state: cardId ? { openItem: cardId } : null });
  };

  // Since you were here: the visit before this one (users/{uid}.homeVisit)
  const [since, setSince] = useState<number | null>(null);
  const visitRef = useRef<{ visit: HomeVisit | null; stored: HomeVisit | null }>({ visit: null, stored: null });
  visitRef.current.stored = (userRecord?.homeVisit as HomeVisit | undefined) ?? null;
  useEffect(() => {
    if (!user || userRecordLoading) return;
    const save = (visit: HomeVisit) => {
      visitRef.current.visit = visit;
      updateDoc(doc(db, "users", user.uid), { homeVisit: visit }).catch((e) =>
        console.error("Could not save visit:", e),
      );
    };
    // Opening Home, or coming back to the tab: a new visit after a long enough gap
    const open = () => {
      const { visit, since } = nextVisit(visitRef.current.visit ?? visitRef.current.stored);
      setSince(since);
      save(visit);
    };
    // Leaving: remember when they were last here, so the gap counts from then
    const leave = () => {
      const visit = visitRef.current.visit;
      if (visit) save({ ...visit, at: Date.now() });
    };
    const onVisibility = () => (document.visibilityState === "visible" ? open() : leave());
    if (document.visibilityState === "visible") open();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      leave();
    };
    // userRecord is read through visitRef: its own writes shouldn't restart this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userRecordLoading]);

  const name = firstName(user);
  // The headline: picked from what's going on, the day, and the hour (utils/home.ts greeting)
  const greetingText = useMemo(() => {
    const now = new Date();
    const interviewToday = cards.some((card) => {
      const d = stageDeadline(card, now);
      return d?.type === "interview" && d.source !== "email" && deadlineDays(d, now) === 0;
    });
    const newOffer =
      since !== null &&
      cards.some((card) =>
        (card.history || []).some((e) => e.stage === "offer" && e.source !== "manual" && Date.parse(e.at) > since),
      );
    const goal = typeof userRecord?.weeklyGoal === "number" ? userRecord.weeklyGoal : DEFAULT_WEEKLY_GOAL;
    return greeting(
      name,
      {
        interviewToday,
        newOffer,
        justJoined: joinedAt > 0 && now.getTime() - joinedAt < 86400000,
        goalHit: weeklyGoal(cards, goal, now).hit,
        awayDays: since !== null ? (now.getTime() - since) / 86400000 : null,
      },
      now,
    );
    // minuteTick: morning turns into afternoon while the page is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, cards, since, userRecord?.onboardingCompletedAt, userRecord?.weeklyGoal, minuteTick]);

  // What a new user's first sync found: once, after that sync
  const reconnect = () => {
    if (!user) return;
    beginGmailOAuth({
      user,
      apiBase: process.env.REACT_APP_API_URL ?? "http://localhost:5050",
      successRedirect: `${window.location.origin}/dashboard/home`,
      errorRedirect: `${window.location.origin}/dashboard/home`,
    }).catch((e) => console.error("Could not start Gmail connect:", e));
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // The top bar only shows the sync status; the greeting sits at the top of the page
  const header = needsReconnect ? (
    <button
      type="button"
      onClick={reconnect}
      className="flex items-center gap-2 whitespace-nowrap font-plex text-xs text-melt-text2 underline-offset-2 hover:underline"
    >
      <span className="h-[7px] w-[7px] rounded-full bg-melt-caramel" />
      Reconnect to keep syncing
    </button>
  ) : syncing || lastSync > 0 ? (
    <div className="flex items-center gap-2 whitespace-nowrap font-plex text-xs text-melt-text3">
      <span className={cn("h-[7px] w-[7px] rounded-full bg-[#6E9C86]", syncing && "animate-pulse")} />
      {syncing ? "Syncing…" : `synced ${timeAgo(lastSync)}`}
    </div>
  ) : null;

  useEffect(() => {
    setNavbarActions(header);
    // `header` is rebuilt every render; these are the values it shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNavbarActions, user, minuteTick, lastSync, syncing, needsReconnect]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  return (
    <div className="-mt-2 w-full pb-8 font-grotesk md:-mt-4">
      {/* -mt: the greeting sits closer to the top bar than the layout's padding puts it */}
      <p className="text-[13px] text-melt-text3">{today}</p>
      <h1
        className={cn(
          "text-[30px] font-semibold leading-tight tracking-[-.03em] text-melt-text1",
          // And until the cards are in, so "Good morning" doesn't turn into "Interview day"
          (!authReady || cardsLoading) && "invisible",
        )}
      >
        {greetingText}
      </h1>
      {since !== null && !cardsLoading && !companiesLoading && (
        <SinceStrip since={since} items={sinceSummary(cards, companies, since, new Date(), joinedAt)} />
      )}

      <div className="mt-5 flex flex-col gap-4">
        {banner && (
          <AttentionBanner
            banner={banner}
            expanded={bannerExpanded && banner.more > 0}
            onToggle={() => setBannerExpanded((v) => !v)}
            onDone={(e) => saveAuto(e.todo, { status: "done", hidden: false })}
            onOpen={(e) => openAuto(e.todo)}
            onHide={(e) => saveAuto(e.todo, { bannerHiddenUntil: bannerReturnDay(e), bannerHidden: false })}
            onPrep={(e) =>
              e.item.kind === "oa" && isCodingOa(e.item.card) && !prep.eventDocs[e.item.card.id]?.selfPrep
                ? // Set up: its plan is already running there (usePrepPlan works OAs in)
                  { label: prep.settings ? "Continue prep" : "Start prep", go: () => navigate("/dashboard/prep") }
                : undefined
            }
          />
        )}
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <div className="flex flex-col gap-4">
            <TasksCard
              user={user}
              autoTasks={listedAuto}
              saveAuto={saveAuto}
              onOpenAuto={openAuto}
              firstSync={firstSync}
            />
            <NewRolesCard
              user={user}
              onEditRoles={() => navigate("/settings/job-search")}
              onSeeAll={(w) => navigate(`/dashboard/roles?window=${w}`)}
            />
          </div>
          <div className="flex flex-col gap-4">
            <SeasonCard
              cards={cards}
              loading={cardsLoading}
              firstSync={firstSync}
              weeklyGoalSetting={typeof userRecord?.weeklyGoal === "number" ? userRecord.weeklyGoal : undefined}
              onSetGoal={setGoal}
              onOpen={() => navigate("/dashboard/tracker")}
            />
            <WatchlistCard
              companies={companies}
              loading={companiesLoading}
              onOpen={() => navigate("/dashboard/dreamcompanies")}
              onOpenCompany={(id) =>
                navigate("/dashboard/dreamcompanies", {
                  state: { openCompany: id },
                })
              }
            />
          </div>
        </div>
      </div>
      <GoalDialog
        open={goalOpen}
        goal={typeof userRecord?.weeklyGoal === "number" ? userRecord.weeklyGoal : DEFAULT_WEEKLY_GOAL}
        onSave={setGoal}
        onClose={() => setGoalOpen(false)}
      />
      <FeedbackDialog
        open={feedbackOpen}
        plan={userRecord?.planSource ? String(userRecord.planSource) : String(userRecord?.plan ?? "")}
        startKind="improvement"
        onClose={closeFeedback}
      />
    </div>
  );
}
